/** A user-initiated, tab-only silent recording. Call start directly from a click. */
const MAX_VIDEO_BYTES = 20 * 1024 * 1024
const STOP_AFTER_MS = 59_000

export interface ReportRecording {
  stop(): Promise<Blob>
  cancel(): void
}

export function reportRecordingAvailable() {
  return typeof navigator.mediaDevices?.getDisplayMedia === 'function' &&
    typeof MediaRecorder !== 'undefined' &&
    typeof MediaRecorder.isTypeSupported === 'function'
}

async function videoDimensions(track: MediaStreamTrack, signal?: AbortSignal) {
  const preview = document.createElement('video')
  preview.muted = true
  preview.playsInline = true
  preview.srcObject = new MediaStream([track])
  try {
    return await new Promise<{ width: number; height: number } | null>(resolve => {
      let settled = false
      const finish = (dimensions: { width: number; height: number } | null) => {
        if (settled) return
        settled = true
        window.clearTimeout(timeout)
        preview.removeEventListener('loadedmetadata', check)
        preview.removeEventListener('resize', check)
        signal?.removeEventListener('abort', aborted)
        resolve(dimensions)
      }
      const check = () => {
        if (preview.videoWidth && preview.videoHeight) {
          finish({ width: preview.videoWidth, height: preview.videoHeight })
        }
      }
      const aborted = () => finish(null)
      const timeout = window.setTimeout(() => finish(null), 2_000)
      preview.addEventListener('loadedmetadata', check)
      preview.addEventListener('resize', check)
      signal?.addEventListener('abort', aborted, { once: true })
      // Metadata can still arrive when autoplay is blocked.
      void preview.play().then(check, () => undefined)
      check()
    })
  } finally {
    preview.pause()
    preview.srcObject = null
  }
}

export async function startReportRecording({
  signal,
  onFinished,
  onError,
  onProgress,
}: {
  signal?: AbortSignal
  onFinished: (blob: Blob) => void
  onError?: (error: Error) => void
  onProgress?: (elapsed: number) => void
}): Promise<ReportRecording> {
  if (signal?.aborted || !reportRecordingAvailable()) throw new Error('Recording unavailable')
  const mime = [
    'video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/mp4;codecs=avc1.42E01E',
  ].find(value => MediaRecorder.isTypeSupported(value))
  if (!mime) throw new Error('Recording format unavailable')

  // Must start before the first await: browsers require transient user activation.
  const options: DisplayMediaStreamOptions & {
    preferCurrentTab: boolean
    selfBrowserSurface: 'include'
  } = {
    video: { displaySurface: 'browser' }, audio: false,
    preferCurrentTab: true, selfBrowserSurface: 'include',
  }
  const selected = await navigator.mediaDevices.getDisplayMedia(options)
  const tracks = selected.getTracks()
  const closeTracks = () => tracks.forEach(track => track.stop())
  const video = selected.getVideoTracks()
  tracks.filter(track => track.kind !== 'video').forEach(track => track.stop())
  if (signal?.aborted || video.length !== 1 ||
    video[0].getSettings().displaySurface !== 'browser') {
    closeTracks()
    throw new Error('Choose a browser tab to record')
  }
  // Chrome can resolve the tab picker before the first captured frame has
  // populated width/height. Keep the already approved tab briefly while its
  // settings settle; never relax the tab-only or size limits.
  tracks.filter(track => track.kind !== 'video').forEach(track => track.stop())
  let size = video[0].getSettings()
  for (let attempt = 0; attempt < 40 && (!size.width || !size.height); attempt += 1) {
    if (signal?.aborted) {
      closeTracks()
      throw new Error('Recording cancelled')
    }
    await new Promise<void>(resolve => window.setTimeout(resolve, 50))
    size = video[0].getSettings()
  }
  if ((size.width && size.width > 3840) || (size.height && size.height > 2160)) {
    closeTracks()
    throw new Error('Recording dimensions unavailable')
  }
  if ((!size.width || !size.height) && !signal?.aborted) {
    try {
      const metadata = await videoDimensions(video[0], signal)
      if (metadata) size = { ...size, ...metadata }
    } catch (error) {
      closeTracks()
      throw error
    }
  }
  if (signal?.aborted) {
    closeTracks()
    throw new Error('Recording cancelled')
  }
  if (!size.width || !size.height || size.width > 3840 || size.height > 2160) {
    closeTracks()
    throw new Error('Recording dimensions unavailable')
  }
  // Explicitly discard unexpected audio tracks even if a browser ignored audio:false.
  const recorderStream = new MediaStream([video[0]])
  const recorder = new MediaRecorder(recorderStream, { mimeType: mime, videoBitsPerSecond: 2_000_000 })
  const chunks: Blob[] = []
  let bytes = 0
  let stopped = false
  let cancelled = false
  let timeout = 0
  let ticker = 0
  let finishPromise: Promise<Blob> | null = null
  const started = Date.now()
  const clear = () => {
    window.clearTimeout(timeout)
    window.clearInterval(ticker)
    signal?.removeEventListener('abort', cancel)
    video[0].onended = null
  }
  const cancel = () => {
    if (cancelled) return
    cancelled = true
    clear()
    closeTracks()
    if (recorder.state !== 'inactive') recorder.stop()
  }
  const stop = () => {
    if (cancelled) return Promise.reject(new Error('Recording cancelled'))
    if (finishPromise) return finishPromise
    finishPromise = new Promise<Blob>((resolve, reject) => {
      recorder.onstop = () => {
        clear(); closeTracks()
        if (cancelled || bytes === 0 || bytes > MAX_VIDEO_BYTES) {
          reject(new Error('Recording cancelled or too large'))
          return
        }
        resolve(new Blob(chunks, { type: mime.split(';', 1)[0] }))
      }
      recorder.onerror = () => { cancel(); reject(new Error('Recording failed')) }
      try {
        stopped = true
        recorder.stop()
      } catch (error) {
        cancel()
        reject(error instanceof Error ? error : new Error('Recording failed'))
      }
    })
    return finishPromise
  }
  recorder.ondataavailable = (event) => {
    if (cancelled || !event.data.size) return
    bytes += event.data.size
    if (bytes > MAX_VIDEO_BYTES) {
      cancel()
      onError?.(new Error('Recording exceeds 20 MB'))
      return
    }
    chunks.push(event.data)
  }
  const finishAutomatically = () => {
    if (stopped || cancelled) return
    void stop().then(onFinished, error => onError?.(error instanceof Error ? error : new Error('Recording failed')))
  }
  video[0].onended = finishAutomatically
  signal?.addEventListener('abort', cancel, { once: true })
  try {
    recorder.start(1000)
  } catch (error) {
    cancel()
    throw error
  }
  timeout = window.setTimeout(finishAutomatically, STOP_AFTER_MS)
  ticker = window.setInterval(() => onProgress?.(Math.min(59, Math.floor((Date.now() - started) / 1000))), 1000)
  return { stop, cancel }
}
